import { useEffect } from 'react';

import { getVisitKey, VisitKeyLike } from '@utils/getVisitKey';

type SelectableVisit = VisitKeyLike & {
  visitOrder?: number;
};

/**
 * Default-selects the smallest-`visitOrder` visit, preferring visits whose key is
 * in `interactiveKeys` (visits that have data). Re-selects when nothing is
 * selected, the selection is gone, or it has no data while interactive visits
 * exist (e.g. the selection happened before the data response arrived).
 */
export const useDefaultVisitSelection = <T extends SelectableVisit>(
  visits: T[],
  selectedVisit: string,
  setSelectedVisit: (key: string) => void,
  interactiveKeys: Set<string>
) => {
  useEffect(() => {
    if (visits.length === 0) return;

    const currentVisitExists = visits.some(
      visit => getVisitKey(visit) === selectedVisit
    );
    const selectableVisits = visits.filter(visit =>
      interactiveKeys.has(getVisitKey(visit))
    );
    const selectedHasData = interactiveKeys.has(selectedVisit);

    if (
      !selectedVisit ||
      !currentVisitExists ||
      (!selectedHasData && selectableVisits.length > 0)
    ) {
      const candidates = selectableVisits.length ? selectableVisits : visits;
      const firstVisit = candidates.reduce((min, visit) => {
        const visitOrder = visit.visitOrder ?? Infinity;
        const minOrder = min.visitOrder ?? Infinity;
        return visitOrder < minOrder ? visit : min;
      });
      setSelectedVisit(getVisitKey(firstVisit));
    }
  }, [visits, selectedVisit, interactiveKeys, setSelectedVisit]);
};
